import { createContext, useContext, useEffect } from 'react';
import useAppStore from '../stores/useAppStore';

const ThemeContext = createContext();

function resolveDark(pref) {
  if (pref === 'dark') return true;
  if (pref === 'light') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

export function ThemeProvider({ children }) {
  const themePreference = useAppStore((s) => s.themePreference);
  const setThemePreference = useAppStore((s) => s.setThemePreference);
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);

  // Apply current preference + sync system changes
  useEffect(() => {
    const isDark = resolveDark(themePreference);
    setNightMode(isDark);
    const root = document.documentElement;
    root.classList.toggle('dark', isDark);
    if (isDark) root.setAttribute('data-theme', 'dark');
    else root.setAttribute('data-theme', 'light');
    const themeColor = isDark ? '#0f172a' : '#f0f0f5';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor);
  }, [themePreference, setNightMode]);

  // Follow system preference live when set to 'system'
  useEffect(() => {
    if (themePreference !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const root = document.documentElement;
      root.classList.toggle('dark', e.matches);
      root.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      setNightMode(e.matches);
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [themePreference, setNightMode]);

  const value = {
    theme: themePreference,
    setTheme: setThemePreference,
    nightMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
